import { isDefaultUpvalueEmoji } from './upvalueEmoji'

// Most-recent-first emoji reactions the current user has actually sent.
// Stored per user so shared devices don't leak reaction habits across accounts.
export const MAX_STORED_RECENT_REACTIONS = 100
export const DEFAULT_DISPLAYED_RECENT_REACTIONS = 12

const RECENT_REACTION_ROW_COUNT = 2
const RECENT_REACTION_MIN_COLUMN_WIDTH = 44
const RECENT_REACTION_COLUMN_GAP = 6

const RECENT_REACTIONS_STORAGE_KEY_PREFIX = 'knov_recent_reaction_emojis'

type RecentReactionStorage = Pick<Storage, 'getItem' | 'setItem'>

export function recentReactionsStorageKey(userId: string | number) {
    return `${RECENT_REACTIONS_STORAGE_KEY_PREFIX}:${userId}`
}

export function recentReactionDisplayCapacity(containerWidth: number): number {
    if (!Number.isFinite(containerWidth) || containerWidth <= 0) {
        return DEFAULT_DISPLAYED_RECENT_REACTIONS
    }

    const columnCount = Math.max(
        1,
        Math.floor(
            (containerWidth + RECENT_REACTION_COLUMN_GAP) /
                (RECENT_REACTION_MIN_COLUMN_WIDTH + RECENT_REACTION_COLUMN_GAP),
        ),
    )

    return columnCount * RECENT_REACTION_ROW_COUNT
}

// The default upvalue arrow is a first-class button everywhere already, so it
// never counts as a "recent" pick.
function isRecordableReactionEmoji(emoji: unknown): emoji is string {
    return typeof emoji === 'string' && !!emoji.trim() && !isDefaultUpvalueEmoji(emoji.trim())
}

export function normalizeRecentReactionEmojis(value: unknown): string[] {
    if (!Array.isArray(value)) return []

    const seenEmojis = new Set<string>()
    const normalizedEmojis: string[] = []

    for (const entry of value) {
        if (!isRecordableReactionEmoji(entry)) continue

        const emoji = entry.trim()
        if (seenEmojis.has(emoji)) continue

        seenEmojis.add(emoji)
        normalizedEmojis.push(emoji)
        if (normalizedEmojis.length >= MAX_STORED_RECENT_REACTIONS) break
    }

    return normalizedEmojis
}

export function pushRecentReactionEmoji(recentEmojis: string[], emoji: unknown): string[] {
    const normalizedRecents = normalizeRecentReactionEmojis(recentEmojis)
    if (!isRecordableReactionEmoji(emoji)) return normalizedRecents

    return normalizeRecentReactionEmojis([emoji.trim(), ...normalizedRecents])
}

export function fillRecentReactionOptionFallbacks<T>(
    resolvedOptions: Array<T | null | undefined>,
    fallbackOptions: T[],
): T[] {
    return fallbackOptions.map((fallbackOption, index) => resolvedOptions[index] ?? fallbackOption)
}

export function readRecentReactionEmojis(
    storage: RecentReactionStorage | null | undefined,
    userId: string | number | null | undefined,
): string[] {
    if (!storage || userId == null) return []

    try {
        const serializedRecents = storage.getItem(recentReactionsStorageKey(userId))
        if (!serializedRecents) return []

        return normalizeRecentReactionEmojis(JSON.parse(serializedRecents))
    } catch {
        return []
    }
}

export function writeRecentReactionEmoji(
    storage: RecentReactionStorage | null | undefined,
    userId: string | number | null | undefined,
    emoji: unknown,
): string[] {
    const currentRecents = readRecentReactionEmojis(storage, userId)
    if (!storage || userId == null || !isRecordableReactionEmoji(emoji)) return currentRecents

    const nextRecents = pushRecentReactionEmoji(currentRecents, emoji)

    try {
        storage.setItem(recentReactionsStorageKey(userId), JSON.stringify(nextRecents))
    } catch {
        // Storage can be unavailable (private mode, quota) — recents are best-effort.
    }

    return nextRecents
}

function browserStorage(): RecentReactionStorage | null {
    try {
        return typeof window !== 'undefined' ? window.localStorage : null
    } catch {
        return null
    }
}

function currentUserId(): string | number | null {
    return typeof gon !== 'undefined' ? gon?.currentUser?.id ?? null : null
}

export function getRecentReactionEmojis(): string[] {
    return readRecentReactionEmojis(browserStorage(), currentUserId())
}

export function recordRecentReactionEmoji(emoji?: string | null) {
    writeRecentReactionEmoji(browserStorage(), currentUserId(), emoji)
}
